import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import MapboxSelector from '../components/MapboxSelector';
import ImageTagEditor from '../components/ImageTagEditor';
import {
  Save,
  Trash2,
  ArrowLeft,
  Image as ImageIcon,
  MapPin,
  Settings,
  Home,
  Camera,
  Upload,
  AlertCircle,
  CheckCircle,
  TreePine,
  Droplets,
  Shield,
  Zap,
  Wifi,
  Car,
  Users,
  Sun,
  Wind,
  Flame,
  Thermometer,
  Bath,
  BedDouble,
  Layers,
  Maximize2,
  Check,
  Info,
} from 'lucide-react';
import { buildAssetUrl } from '../utils/api';

export default function EditarPropiedad() {
  const { id } = useParams();
  const navigate = useNavigate();
  const usuario = JSON.parse(localStorage.getItem('usuario'));

  // Estados
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Datos principales - CORREGIDO para coincidir con VerPropiedad
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    ubicacion: '',
    precio: '',
    zona: 'Centro',
    caracteristicas: {
      // Números
      recamaras: 0,
      banos: 0,
      medios_banos: 0,
      area: 0,
      terreno: 0,
      estacionamiento: 0,
      niveles: 1,

      // Booleanos - Coincidir con VerPropiedad
      jardin: false,
      terraza: false,
      alberca: false,
      elevador: false,
      cisterna: false,
      bodega: false,
      cuarto_servicio: false,
      acceso_discapacitados: false,
      paneles_solares: false,
      aire_acondicionado: false,
      ventiladores: false,
      calentador_agua: false,
      gas_estacionario: false,
      amueblado: false,
      cocina_integral: false,
      cocina_equipada: false,
      vigilancia: false,
      seguridad: false,
      cerca_electrica: false,
      interfon: false,
      internet: false,
      fibra_optica: false,
      pet_friendly: false,
      uso_comercial: false,

      // Textos y selects
      estado_conservacion: 'bueno',
      antiguedad: '1-5',
      tipo_construccion: 'tradicional',
      orientacion: '',
      tipo_agua: 'municipal',
      tipo_operacion: 'venta',
    },
  });

  // Coordenadas para Mapbox - [longitud, latitud]
  const [coordenadas, setCoordenadas] = useState([-89.62, 20.97]); // Mérida por defecto
  const [fotoPrincipal, setFotoPrincipal] = useState(null);
  const [fotoPrincipalPreview, setFotoPrincipalPreview] = useState('');
  const [imagenes, setImagenes] = useState([]);
  const [originalData, setOriginalData] = useState(null);
  const submitLockRef = useRef(false);

  // Función para construir URL de imagen (igual que en VerPropiedad)
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;

    if (imagePath.startsWith('http')) {
      return imagePath;
    }

    if (imagePath.startsWith('/uploads')) {
      return buildAssetUrl(imagePath);
    }

    if (!imagePath.startsWith('/')) {
      return buildAssetUrl(imagePath);
    }

    // Por defecto
    return buildAssetUrl(imagePath);
  };

  // Cargar datos
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setLoading(true);
        setError('');

        console.log(`Cargando propiedad ${id}...`);
        const response = await api.get(`/properties/${id}`);
        const propiedad = response.data;
        console.log('Propiedad cargada:', propiedad);

        const esSuperadmin = usuario.rol === 'superadmin';
        const esAsesorPropietario = propiedad.asesor_id === usuario.id;

        if (!esSuperadmin && !esAsesorPropietario) {
          setError('No tienes permisos para editar esta propiedad');
          setLoading(false);
          return;
        }

        // Guardar datos originales para comparar
        setOriginalData(propiedad);

        // Transformar características para asegurar consistencia
        const caracteristicasTransformadas = {
          // Valores por defecto
          recamaras: 0,
          banos: 0,
          medios_banos: 0,
          area: 0,
          terreno: 0,
          estacionamiento: 0,
          niveles: 1,
          jardin: false,
          terraza: false,
          alberca: false,
          // ... otros booleanos con false por defecto
          estado_conservacion: 'bueno',
          antiguedad: '1-5',
          tipo_construccion: 'tradicional',
          orientacion: '',
          tipo_agua: 'municipal',
          tipo_operacion: 'venta',

          // Sobrescribir con datos reales
          ...(propiedad.caracteristicas || {}),
        };

        // Actualizar estado principal - CORREGIDO
        setFormData({
          titulo: propiedad.titulo || '',
          descripcion: propiedad.descripcion || '',
          ubicacion: propiedad.ubicacion || '',
          precio: propiedad.precio?.toString() || '',
          zona: propiedad.zona || 'Centro',
          caracteristicas: caracteristicasTransformadas,
        });

        console.log('FormData actualizado:', {
          titulo: propiedad.titulo || '',
          precio: propiedad.precio?.toString() || '',
          caracteristicas: caracteristicasTransformadas,
        });

        // Coordenadas - USAR MISMO FORMATO QUE VerPlano
        if (propiedad.lng && propiedad.lat) {
          // Si ya viene en formato lng/lat
          setCoordenadas([parseFloat(propiedad.lng), parseFloat(propiedad.lat)]);
        } else if (propiedad.pos_x && propiedad.pos_y) {
          // Si viene como pos_x/pos_y (longitud/latitud)
          setCoordenadas([parseFloat(propiedad.pos_x), parseFloat(propiedad.pos_y)]);
        } else if (propiedad.coordinates && Array.isArray(propiedad.coordinates)) {
          // Si ya viene en formato Mapbox [lng, lat]
          setCoordenadas(propiedad.coordinates);
        }

        // Foto principal - USAR getImageUrl()
        if (propiedad.foto_principal) {
          const urlCompleta = getImageUrl(propiedad.foto_principal);
          console.log('Foto principal URL:', urlCompleta);
          setFotoPrincipalPreview(urlCompleta);
        }

        // Imágenes - CORREGIDO: Parsear como en VerPropiedad
        let imagenesParseadas = [];
        if (propiedad.imagenes && Array.isArray(propiedad.imagenes)) {
          imagenesParseadas = propiedad.imagenes.map((img) => {
            try {
              // Si es string JSON, parsearlo
              if (typeof img === 'string' && img.trim().startsWith('{')) {
                const parsed = JSON.parse(img);
                return {
                  ...parsed,
                  url: getImageUrl(parsed.url || parsed),
                  originalUrl: parsed.url || parsed,
                };
              }
              // Si ya es objeto
              return {
                ...img,
                url: getImageUrl(img.url || img),
                originalUrl: img.url || img,
              };
            } catch (error) {
              console.error('Error parseando imagen:', img, error);
              return {
                url: getImageUrl(img),
                descripcion: '',
                tags: [],
                orden: 0,
                originalUrl: img,
              };
            }
          });
        }

        console.log('Imágenes parseadas:', imagenesParseadas);
        setImagenes(imagenesParseadas);
      } catch (err) {
        console.error('Error cargando propiedad:', err);
        setError('Error al cargar la propiedad. Intenta de nuevo.');
      } finally {
        setLoading(false);
      }
    };

    cargarDatos();
  }, [id, usuario.id, usuario.rol]);

  // Handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCaracteristicaChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      caracteristicas: {
        ...prev.caracteristicas,
        [name]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || 0 : value,
      },
    }));
  };

  const handleSelectChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      caracteristicas: {
        ...prev.caracteristicas,
        [field]: value,
      },
    }));
  };

  const handleMapSelect = useCallback((coords) => {
    console.log('Coordenadas seleccionadas:', coords);
    setCoordenadas(coords);
  }, []);

  // Subir imagen al servidor
  const subirImagen = async (archivo) => {
    try {
      const formDataImg = new FormData();
      formDataImg.append('imagenes', archivo);

      const res = await api.post('/properties/upload', formDataImg, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      console.log('Imagen subida:', res.data);
      return res.data.urls[0]; // Devuelve la ruta relativa
    } catch (error) {
      console.error('Error subiendo imagen:', error);
      throw error;
    }
  };

  // Guardar cambios - CORREGIDO
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (saving || submitLockRef.current) {
      return;
    }

    console.log('Iniciando guardado...');

    // Validaciones
    if (!coordenadas || coordenadas.length !== 2) {
      setError('Selecciona una ubicación válida en el mapa');
      return;
    }

    if (!formData.titulo.trim()) {
      setError('El título es obligatorio');
      return;
    }

    if (!formData.ubicacion.trim()) {
      setError('La ubicación es obligatoria');
      return;
    }

    if (!formData.precio || parseFloat(formData.precio) <= 0) {
      setError('Ingresa un precio válido');
      return;
    }

    submitLockRef.current = true;

    try {
      setSaving(true);
      setError('');
      setSuccess('Guardando cambios...');

      console.log('Preparando datos para enviar...');

      // 1. Subir nueva foto principal si existe
      let fotoPrincipalUrl = originalData?.foto_principal || '';
      if (fotoPrincipal) {
        console.log('Subiendo nueva foto principal...');
        fotoPrincipalUrl = await subirImagen(fotoPrincipal);
      }

      // 2. Procesar imágenes
      const imagenesFinales = [];

      // Imágenes existentes (mantener las que no han cambiado)
      const existentes = imagenes.filter((img) => img.originalUrl && !img.file);
      existentes.forEach((img) => {
        imagenesFinales.push({
          url: img.originalUrl, // Usar la URL original (relativa)
          descripcion: img.descripcion || '',
          tags: img.tags || [],
          orden: img.orden || 0,
        });
      });

      // Imágenes nuevas
      const nuevas = imagenes.filter((img) => img.file);
      console.log(`Subiendo ${nuevas.length} imágenes nuevas...`);
      for (const img of nuevas) {
        const url = await subirImagen(img.file);
        imagenesFinales.push({
          url,
          descripcion: img.descripcion || '',
          tags: img.tags || [],
          orden: img.orden || 0,
        });
      }

      // 3. Preparar datos para enviar - FORMATO COMPATIBLE CON VerPropiedad
      const datosEnviar = {
        titulo: formData.titulo.trim(),
        descripcion: formData.descripcion.trim(),
        ubicacion: formData.ubicacion.trim(),
        precio: parseFloat(formData.precio),
        foto_principal: fotoPrincipalUrl,
        imagenes: imagenesFinales,
        // Enviar en todos los formatos posibles para compatibilidad
        pos_x: coordenadas[0], // longitud
        pos_y: coordenadas[1], // latitud
        lng: coordenadas[0],
        lat: coordenadas[1],
        coordinates: coordenadas,
        zona: formData.zona,
        caracteristicas: formData.caracteristicas,
        // Mantener el asesor original si existe
        asesor_id: originalData?.asesor_id || usuario.id,
      };

      console.log('Datos a enviar:', datosEnviar);

      // 4. Enviar al servidor
      console.log('Enviando PUT a /properties/' + id);
      const response = await api.put(`/properties/${id}`, datosEnviar);
      console.log('Respuesta del servidor:', response.data);

      setSuccess('✅ Propiedad actualizada correctamente');

      // Redirigir después de un breve delay
      setTimeout(() => {
        navigate(`/propiedad/${id}`);
      }, 1500);
    } catch (err) {
      console.error('Error guardando:', err);
      setError(err.response?.data?.error || 'Error al guardar cambios. Intenta de nuevo.');
      setSuccess('');
    } finally {
      setSaving(false);
      submitLockRef.current = false;
    }
  };

  // Eliminar propiedad
  const handleDelete = async () => {
    if (
      !window.confirm('¿Estás seguro de eliminar esta propiedad? Esta acción no se puede deshacer.')
    ) {
      return;
    }

    try {
      setSaving(true);
      await api.delete(`/properties/${id}`);
      setSuccess('Propiedad eliminada exitosamente');
      setTimeout(() => navigate('/propiedades'), 1000);
    } catch (err) {
      setError('Error al eliminar la propiedad. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  // Mostrar loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-500">Cargando propiedad...</p>
        </div>
      </div>
    );
  }

  // Mostrar error de permisos
  if (error && error.includes('permisos')) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-lg">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Acceso denegado</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  // Opciones para selects - COINCIDIR CON VerPropiedad
  const opciones = {
    estado: [
      { valor: 'excelente', label: 'Excelente' },
      { valor: 'bueno', label: 'Bueno' },
      { valor: 'regular', label: 'Regular' },
      { valor: 'requiere_remodelacion', label: 'Requiere remodelación' },
    ],
    antiguedad: [
      { valor: 'nueva', label: 'Menos de 1 año' },
      { valor: '1-5', label: '1-5 años' },
      { valor: '5-10', label: '5-10 años' },
      { valor: '10-20', label: '10-20 años' },
      { valor: '20+', label: 'Más de 20 años' },
    ],
    construccion: [
      { valor: 'tradicional', label: 'Tradicional' },
      { valor: 'contemporanea', label: 'Contemporánea' },
      { valor: 'moderna', label: 'Moderna' },
      { valor: 'rustica', label: 'Rústica' },
      { valor: 'industrial', label: 'Industrial' },
    ],
    agua: [
      { valor: 'municipal', label: 'Municipal' },
      { valor: 'pozo', label: 'Pozo' },
      { valor: 'ambas', label: 'Ambas' },
    ],
    operacion: [
      { valor: 'venta', label: 'Venta' },
      { valor: 'renta', label: 'Renta' },
      { valor: 'renta_venta', label: 'Renta con opción a compra' },
    ],
    orientacion: [
      { valor: '', label: 'Seleccionar' },
      { valor: 'norte', label: 'Norte' },
      { valor: 'sur', label: 'Sur' },
      { valor: 'este', label: 'Este' },
      { valor: 'oeste', label: 'Oeste' },
      { valor: 'noreste', label: 'Noreste' },
      { valor: 'noroeste', label: 'Noroeste' },
      { valor: 'sureste', label: 'Sureste' },
      { valor: 'suroeste', label: 'Suroeste' },
    ],
  };

  // Grupos de características - ORDENADOS COMO EN VerPropiedad
  const gruposCaracteristicas = [
    {
      titulo: 'Distribución',
      icon: <Layers size={20} />,
      campos: [
        { nombre: 'recamaras', label: 'Recámaras', tipo: 'number', icon: <BedDouble size={16} /> },
        { nombre: 'banos', label: 'Baños', tipo: 'number', icon: <Bath size={16} /> },
        { nombre: 'medios_banos', label: 'Medios baños', tipo: 'number', icon: <Bath size={16} /> },
        { nombre: 'niveles', label: 'Niveles', tipo: 'number', icon: <Layers size={16} /> },
      ],
    },
    {
      titulo: 'Dimensiones',
      icon: <Maximize2 size={20} />,
      campos: [
        {
          nombre: 'area',
          label: 'Área construida (m²)',
          tipo: 'number',
          icon: <Maximize2 size={16} />,
        },
        { nombre: 'terreno', label: 'Terreno (m²)', tipo: 'number', icon: <Maximize2 size={16} /> },
        {
          nombre: 'estacionamiento',
          label: 'Estacionamientos',
          tipo: 'number',
          icon: <Car size={16} />,
        },
      ],
    },
    {
      titulo: 'Amenidades Principales',
      icon: <Check size={20} />,
      campos: [
        { nombre: 'jardin', label: 'Jardín', tipo: 'checkbox', icon: <TreePine size={16} /> },
        { nombre: 'alberca', label: 'Alberca', tipo: 'checkbox', icon: <Droplets size={16} /> },
        { nombre: 'terraza', label: 'Terraza', tipo: 'checkbox', icon: <Sun size={16} /> },
        { nombre: 'elevador', label: 'Elevador', tipo: 'checkbox', icon: <Layers size={16} /> },
      ],
    },
    {
      titulo: 'Climatización',
      icon: <Thermometer size={20} />,
      campos: [
        {
          nombre: 'aire_acondicionado',
          label: 'Aire acondicionado',
          tipo: 'checkbox',
          icon: <Thermometer size={16} />,
        },
        {
          nombre: 'ventiladores',
          label: 'Ventiladores',
          tipo: 'checkbox',
          icon: <Wind size={16} />,
        },
        {
          nombre: 'calentador_agua',
          label: 'Calentador de agua',
          tipo: 'checkbox',
          icon: <Flame size={16} />,
        },
      ],
    },
    {
      titulo: 'Seguridad',
      icon: <Shield size={20} />,
      campos: [
        { nombre: 'vigilancia', label: 'Vigilancia', tipo: 'checkbox', icon: <Shield size={16} /> },
        {
          nombre: 'seguridad',
          label: 'Sistema de seguridad',
          tipo: 'checkbox',
          icon: <Shield size={16} />,
        },
        {
          nombre: 'cerca_electrica',
          label: 'Cerca eléctrica',
          tipo: 'checkbox',
          icon: <Zap size={16} />,
        },
        { nombre: 'interfon', label: 'Interfon', tipo: 'checkbox', icon: <Users size={16} /> },
      ],
    },
    {
      titulo: 'Servicios',
      icon: <Zap size={20} />,
      campos: [
        { nombre: 'internet', label: 'Internet', tipo: 'checkbox', icon: <Wifi size={16} /> },
        {
          nombre: 'fibra_optica',
          label: 'Fibra óptica',
          tipo: 'checkbox',
          icon: <Wifi size={16} />,
        },
        {
          nombre: 'paneles_solares',
          label: 'Paneles solares',
          tipo: 'checkbox',
          icon: <Zap size={16} />,
        },
        {
          nombre: 'gas_estacionario',
          label: 'Gas estacionario',
          tipo: 'checkbox',
          icon: <Flame size={16} />,
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pt-20 pb-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start mb-6 gap-4">
            <div className="flex-1">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 hover:bg-slate-100 px-3 py-1.5 rounded-lg transition-colors w-fit"
              >
                <ArrowLeft size={18} />
                <span className="text-sm font-medium">Volver a la propiedad</span>
              </button>
              <h1 className="text-3xl font-bold text-slate-900">Editar Propiedad</h1>
              <div className="flex items-center gap-4 mt-2">
                <p className="text-slate-500">ID: {id}</p>
                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                  {formData.caracteristicas.tipo_operacion === 'venta'
                    ? 'Venta'
                    : formData.caracteristicas.tipo_operacion === 'renta'
                      ? 'Renta'
                      : 'Renta/Venta'}
                </span>
                <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded">
                  {formData.zona}
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleDelete}
                disabled={saving}
                className="px-4 py-2.5 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors flex items-center gap-2"
                title="Eliminar propiedad"
              >
                <Trash2 size={18} />
                <span className="hidden sm:inline">Eliminar</span>
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 transition-colors shadow-sm min-w-[120px] justify-center"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Guardando...</span>
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    <span>Guardar</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Mensajes de error y éxito */}
          {error && !error.includes('permisos') && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg animate-in fade-in">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-red-700 font-medium">{error}</p>
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg animate-in fade-in">
              <div className="flex items-start gap-3">
                <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-green-700 font-medium">{success}</p>
                  <p className="text-green-600 text-sm mt-1">Redirigiendo...</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Sección 1: Información básica */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
              <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                <Home size={20} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Información básica</h2>
                <p className="text-sm text-slate-500 mt-1">Datos principales de la propiedad</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Título de la propiedad *
                </label>
                <input
                  type="text"
                  name="titulo"
                  value={formData.titulo}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Ej: Casa moderna en zona norte"
                />
                <p className="text-xs text-slate-500 mt-2">Título descriptivo y atractivo</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Precio (MXN) *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-slate-500 font-medium">$</span>
                  <input
                    type="number"
                    name="precio"
                    value={formData.precio}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="1000"
                    className="w-full pl-8 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Ej: 2500000"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2">Precio en pesos mexicanos</p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Ubicación / Dirección completa *
                </label>
                <input
                  type="text"
                  name="ubicacion"
                  value={formData.ubicacion}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Ej: Calle 60 #123 x 45 y 47, Centro, Mérida"
                />
                <p className="text-xs text-slate-500 mt-2">Dirección exacta para visitas</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Zona</label>
                <select
                  name="zona"
                  value={formData.zona}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                >
                  <option value="Norte">Norte</option>
                  <option value="Centro">Centro</option>
                  <option value="Sur">Sur</option>
                  <option value="Poniente">Poniente</option>
                  <option value="Oriente">Oriente</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Tipo de operación
                </label>
                <select
                  value={formData.caracteristicas.tipo_operacion}
                  onChange={(e) => handleSelectChange('tipo_operacion', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                >
                  {opciones.operacion.map((op) => (
                    <option key={op.valor} value={op.valor}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Descripción detallada
                </label>
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleInputChange}
                  rows="6"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Describe la propiedad, características destacadas, ventajas de la ubicación, distribución de espacios, etc."
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-slate-500">Recomendado: 200-500 caracteres</p>
                  <p
                    className={`text-xs ${formData.descripcion.length > 1000 ? 'text-red-500' : 'text-slate-500'}`}
                  >
                    {formData.descripcion.length}/1000
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sección 2: Características técnicas */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
              <div className="p-2 bg-slate-100 text-slate-600 rounded-lg">
                <Settings size={20} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Información técnica</h2>
                <p className="text-sm text-slate-500 mt-1">Especificaciones de construcción</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Estado de conservación
                </label>
                <select
                  value={formData.caracteristicas.estado_conservacion}
                  onChange={(e) => handleSelectChange('estado_conservacion', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                >
                  {opciones.estado.map((op) => (
                    <option key={op.valor} value={op.valor}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Antigüedad</label>
                <select
                  value={formData.caracteristicas.antiguedad}
                  onChange={(e) => handleSelectChange('antiguedad', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                >
                  {opciones.antiguedad.map((op) => (
                    <option key={op.valor} value={op.valor}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Tipo de construcción
                </label>
                <select
                  value={formData.caracteristicas.tipo_construccion}
                  onChange={(e) => handleSelectChange('tipo_construccion', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                >
                  {opciones.construccion.map((op) => (
                    <option key={op.valor} value={op.valor}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Orientación</label>
                <select
                  value={formData.caracteristicas.orientacion}
                  onChange={(e) => handleSelectChange('orientacion', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                >
                  {opciones.orientacion.map((op) => (
                    <option key={op.valor} value={op.valor}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Tipo de agua
                </label>
                <select
                  value={formData.caracteristicas.tipo_agua}
                  onChange={(e) => handleSelectChange('tipo_agua', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                >
                  {opciones.agua.map((op) => (
                    <option key={op.valor} value={op.valor}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Sección 3: Características por grupos */}
          {gruposCaracteristicas.map((grupo, idx) => (
            <div key={idx} className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-slate-50 text-slate-600 rounded-lg border border-slate-200">
                  {grupo.icon}
                </div>
                <h3 className="text-lg font-semibold text-slate-900">{grupo.titulo}</h3>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {grupo.campos.map((campo) => (
                  <div
                    key={campo.nombre}
                    className={`p-4 border ${formData.caracteristicas[campo.nombre] ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-white'} rounded-lg hover:border-slate-300 transition-colors`}
                  >
                    {campo.tipo === 'checkbox' ? (
                      <label className="flex items-center gap-3 cursor-pointer">
                        <div
                          className={`p-2 rounded ${formData.caracteristicas[campo.nombre] ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`}
                        >
                          {campo.icon}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-slate-700">
                              {campo.label}
                            </span>
                            <input
                              type="checkbox"
                              name={campo.nombre}
                              checked={formData.caracteristicas[campo.nombre] || false}
                              onChange={handleCaracteristicaChange}
                              className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      </label>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="p-1.5 bg-slate-100 text-slate-600 rounded">
                            {campo.icon}
                          </div>
                          <label className="block text-sm font-medium text-slate-700">
                            {campo.label}
                          </label>
                        </div>
                        <input
                          type="number"
                          name={campo.nombre}
                          value={formData.caracteristicas[campo.nombre] || ''}
                          onChange={handleCaracteristicaChange}
                          min="0"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="0"
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Sección 4: Multimedia */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
              <div className="p-2 bg-slate-100 text-slate-600 rounded-lg">
                <ImageIcon size={20} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Multimedia</h2>
                <p className="text-sm text-slate-500 mt-1">Imágenes y ubicación en mapa</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Imagen principal */}
              <div>
                <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                  <Camera size={18} />
                  Imagen principal
                </h3>
                <div className="border-2 border-dashed border-slate-300 rounded-xl p-4 text-center hover:border-blue-400 transition-colors bg-slate-50">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        // Validar tipo de archivo
                        if (!file.type.startsWith('image/')) {
                          setError('Por favor, selecciona un archivo de imagen válido');
                          return;
                        }

                        // Validar tamaño (máximo 5MB)
                        if (file.size > 5 * 1024 * 1024) {
                          setError('La imagen es demasiado grande (máximo 5MB)');
                          return;
                        }

                        setFotoPrincipal(file);
                        setFotoPrincipalPreview(URL.createObjectURL(file));
                      }
                    }}
                    className="hidden"
                    id="foto-principal"
                  />
                  <label htmlFor="foto-principal" className="cursor-pointer block">
                    {fotoPrincipalPreview ? (
                      <div className="relative group">
                        <img
                          src={fotoPrincipalPreview}
                          alt="Preview"
                          className="w-full h-64 object-cover rounded-lg mb-4 shadow-sm"
                        />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                          <div className="text-center text-white p-4">
                            <Camera className="w-8 h-8 mx-auto mb-2" />
                            <p className="font-medium">Cambiar imagen</p>
                            <p className="text-sm text-white/80 mt-1">
                              Haz clic para seleccionar otra
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="py-10">
                        <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                        <p className="text-slate-600 font-medium mb-2">
                          Haz clic para subir foto principal
                        </p>
                        <p className="text-xs text-slate-500">
                          Recomendado: 1200x800px, JPG o PNG, máximo 5MB
                        </p>
                      </div>
                    )}
                  </label>
                </div>
                {fotoPrincipalPreview && (
                  <p className="text-sm text-slate-600 mt-3 flex items-center gap-2">
                    <Check size={14} className="text-green-500" />
                    <span>Imagen principal lista</span>
                  </p>
                )}
              </div>

              {/* Mapa */}
              <div>
                <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                  <MapPin size={18} />
                  Ubicación en mapa
                </h3>
                <div className="h-64 rounded-xl overflow-hidden border border-slate-300 shadow-sm bg-slate-50">
                  <MapboxSelector
                    coordenadasIniciales={coordenadas}
                    onSeleccionarUbicacion={handleMapSelect}
                  />
                </div>
                {coordenadas && (
                  <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-sm text-slate-700 mb-1">
                      <span className="font-medium">Coordenadas actuales:</span>
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-white p-2 rounded border">
                        <span className="text-slate-500">Longitud:</span>
                        <p className="font-mono">{coordenadas[0].toFixed(6)}</p>
                      </div>
                      <div className="bg-white p-2 rounded border">
                        <span className="text-slate-500">Latitud:</span>
                        <p className="font-mono">{coordenadas[1].toFixed(6)}</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      <Info size={10} className="inline mr-1" />
                      Haz clic en el mapa para cambiar la ubicación
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Galería de imágenes */}
            <div className="mt-10">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium">Galería de imágenes</h3>
                <p className="text-sm text-slate-500">
                  {imagenes.length} {imagenes.length === 1 ? 'imagen' : 'imágenes'} en la galería
                </p>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <ImageTagEditor images={imagenes} onImagesChange={setImagenes} />
              </div>

              <div className="mt-4 text-sm text-slate-600">
                <p className="flex items-center gap-2">
                  <Info size={14} />
                  <span>
                    Recomendación: Sube al menos 4-6 imágenes de diferentes áreas de la propiedad
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Botones finales */}
          <div className="flex flex-col sm:flex-row justify-between gap-4 pt-8 border-t border-slate-200">
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={saving}
                className="px-6 py-3 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors w-full sm:w-auto"
              >
                Cancelar
              </button>
              <p className="text-xs text-slate-500">Los cambios no guardados se perderán</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="button"
                onClick={() => navigate(`/propiedad/${id}`)}
                className="px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors w-full sm:w-auto"
              >
                Ver vista previa
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm font-medium w-full sm:w-auto"
              >
                {saving ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Guardando cambios...
                  </span>
                ) : (
                  'Guardar cambios'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
